import axios from '@/libs/axios'
import { getAudiences } from './campaigns'

const BASE_URL = 'https://mc-analytics.manyconvert.com'

/**
 * Busca templates do WhatsApp disponíveis para uma loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de templates
 */
export async function buscarTemplates(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/templates`, {
      params: {
        store_uuid: storeUuid,
      },
    })
    console.log('response.data', response.data)
    return response.data || []
  } catch (error) {
    console.error('Erro ao buscar templates:', error)
    throw error
  }
}

/**
 * Busca audiências disponíveis para uma loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de audiências
 */
export async function buscarAudiencias(storeUuid) {
  try {
    const audiencias = await getAudiences(storeUuid)

    // Transformar audiências em formato de options para o select
    if (Array.isArray(audiencias)) {
      return audiencias.map(aud => ({
        value: aud.id,
        text: aud.name || aud.nome || 'Audiência sem nome',
      }))
    }

    return []
  } catch (error) {
    console.error('Erro ao buscar audiências:', error)
    // Retornar array vazio em caso de erro
    return []
  }
}

/**
 * Cria uma nova campanha de WhatsApp
 * @param {Object} campanha - Dados da campanha
 * @param {File} [campanha.image] - Arquivo de mídia: imagem (JPG, PNG, GIF - máx 5MB) ou vídeo (MP4, MOV, AVI - máx 16MB)
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Campanha criada
 */
export async function criarCampanha(campanha, storeUuid) {
  try {
    // 🎠 Se for template de carrossel, processar imagens dos cards
    if (campanha.carouselCards && Array.isArray(campanha.carouselCards) && campanha.carouselCards.length > 0) {
      const formData = new FormData()

      formData.append('name', campanha.name || '')
      formData.append('template_name', campanha.template_name || '')
      formData.append('audience_id', campanha.audience_id ? String(campanha.audience_id) : '')
      if (campanha.utm_campaign) formData.append('utm_campaign', campanha.utm_campaign)
      if (campanha.coupon_code != null && String(campanha.coupon_code).trim() !== '') {
        formData.append('coupon_code', String(campanha.coupon_code).trim())
      }

      if (campanha.description) formData.append('description', campanha.description)
      if (campanha.send_date) formData.append('send_date', campanha.send_date)
      if (campanha.attribution_window_hours) {
        formData.append('attribution_window_hours', String(campanha.attribution_window_hours))
      }

      formData.append('is_active', 'true')
      formData.append('is_carousel', 'true')

      // 🎠 Adicionar imagens dos cards
      campanha.carouselCards.forEach((card, index) => {
        // Se tiver arquivo (upload novo), usar arquivo
        if (card.imageFile) {
          formData.append(`carousel_image_${index}`, card.imageFile)
        }
        // Se tiver URL (imagem do template), enviar URL
        else if (card.imageUrl) {
          formData.append(`carousel_image_url_${index}`, card.imageUrl)
        }

        // Enviar parâmetro do botão se houver
        if (card.buttonParam) {
          formData.append(`carousel_button_param_${index}`, card.buttonParam)
        }
      })

      console.log('🎠 Enviando campanha com carrossel (FormData)', {
        totalCards: campanha.carouselCards.length,
      })

      const response = await axios.post(`${BASE_URL}/api/stores/${storeUuid}/campaigns`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data.data || response.data
    }

    // Se houver mídia (imagem ou vídeo), usar FormData
    if (campanha.image instanceof File) {
      const formData = new FormData()

      formData.append('name', campanha.name || '')
      formData.append('template_name', campanha.template_name || '')
      formData.append('audience_id', campanha.audience_id ? String(campanha.audience_id) : '')
      if (campanha.utm_campaign) formData.append('utm_campaign', campanha.utm_campaign)
      if (campanha.coupon_code != null && String(campanha.coupon_code).trim() !== '') {
        formData.append('coupon_code', String(campanha.coupon_code).trim())
      }

      if (campanha.description) formData.append('description', campanha.description)
      if (campanha.send_date) formData.append('send_date', campanha.send_date)
      if (campanha.attribution_window_hours) {
        formData.append('attribution_window_hours', String(campanha.attribution_window_hours))
      }

      formData.append('is_active', 'true')

      // Adicionar mídia (imagem ou vídeo) - backend detecta automaticamente o tipo
      formData.append('image', campanha.image)

      const mediaType = campanha.image.type.startsWith('video/') ? 'vídeo' : 'imagem'
      console.log(`📤 Enviando campanha com ${mediaType} (FormData)`)

      const response = await axios.post(`${BASE_URL}/api/stores/${storeUuid}/campaigns`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data.data || response.data
    }

    // Se não houver imagem, usar JSON normal
    const payload = {
      name: campanha.name || '',
      description: campanha.description || '',
      template_name: campanha.template_name || '',
      audience_id: campanha.audience_id ? parseInt(campanha.audience_id) : null,
      send_date: campanha.send_date || null,
      attribution_window_hours: campanha.attribution_window_hours ? parseInt(campanha.attribution_window_hours) : null,
      image_url: (campanha.image_url && campanha.image_url.trim()) ? campanha.image_url : null,
      utm_campaign: campanha.utm_campaign || null,
      coupon_code:
        campanha.coupon_code != null && String(campanha.coupon_code).trim() !== ''
          ? String(campanha.coupon_code).trim()
          : null,
      is_active: true,
    }

    // Verificar se algum campo ficou como undefined (proteção extra)
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        payload[key] = null
      }
    })

    console.log('📤 Enviando campanha (JSON)')

    const response = await axios.post(`${BASE_URL}/api/stores/${storeUuid}/campaigns`, payload)

    // A API pode retornar { success, data } ou apenas os dados
    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao criar campanha:', error)
    throw error
  }
}

/**
 * Atualiza uma campanha existente
 * NOTA: Função desabilitada - Edição de campanhas não é permitida
 * @param {number} id - ID da campanha
 * @param {Object} campanha - Dados atualizados
 * @returns {Promise<Object>} Campanha atualizada
 */
export async function atualizarCampanha(storeUuid, campanhaId, campanha) {
  try {
    // Criar FormData para enviar mídia junto
    const formData = new FormData()

    // Adicionar campos básicos
    formData.append('name', campanha.name)
    formData.append('template_name', campanha.template_name)
    formData.append('audience_id', campanha.audience_id)
    formData.append('send_date', campanha.send_date)
    formData.append('attribution_window_hours', campanha.attribution_window_hours)

    if (campanha.description) {
      formData.append('description', campanha.description)
    }

    if (campanha.utm_campaign) {
      formData.append('utm_campaign', campanha.utm_campaign)
    }

    // Sempre enviar em edição para permitir limpar o cupom no banco
    formData.append(
      'coupon_code',
      campanha.coupon_code != null && String(campanha.coupon_code).trim() !== ''
        ? String(campanha.coupon_code).trim()
        : '',
    )

    // Adicionar imagem/vídeo se houver (upload de nova mídia)
    if (campanha.image) {
      formData.append('image', campanha.image)

      // Se for vídeo, adicionar thumbnail
      if (campanha.videoThumbnail) {
        formData.append('video_thumbnail', campanha.videoThumbnail)
      }
    }

    // Se for carrossel, adicionar dados dos cards
    if (campanha.carouselCards && campanha.carouselCards.length > 0) {
      formData.append('carousel_cards', JSON.stringify(campanha.carouselCards.map(card => ({
        buttonParam: card.buttonParam || '',
      }))))

      // Adicionar imagens dos cards
      campanha.carouselCards.forEach((card, index) => {
        if (card.imageFile) {
          formData.append(`carousel_card_${index}`, card.imageFile)
        } else if (card.imageUrl) {
          formData.append(`carousel_card_${index}_url`, card.imageUrl)
        }
      })
    }

    console.log('📝 Atualizando campanha:', campanhaId)

    const response = await axios.put(
      `${BASE_URL}/api/stores/${storeUuid}/campaigns/${campanhaId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return response.data
  } catch (error) {
    console.error('Erro ao atualizar campanha:', error)
    throw error
  }
}

/**
 * Lista todas as campanhas de uma loja
 * @param {string} storeUuid - UUID da loja
 * @param {boolean} includeDeleted - Se true, inclui campanhas deletadas (default: false)
 * @returns {Promise<Array>} Lista de campanhas
 */
export async function listarCampanhas(storeUuid, includeDeleted = false) {
  try {
    // ✅ V2: Usar endpoint com storeUuid na URL
    const params = {}
    if (includeDeleted) {
      params.include_deleted = 'true'
    }

    console.log('🔍 Listando campanhas com params:', params)

    const response = await axios.get(`${BASE_URL}/api/stores/${storeUuid}/campaigns`, { params })

    console.log('📦 Resposta do backend (primeiras 2 campanhas):', response.data.data?.slice(0, 2))

    // A API retorna { success, data, total }
    return response.data.data || []
  } catch (error) {
    console.error('Erro ao listar campanhas:', error)
    throw error
  }
}

/**
 * Exclui uma campanha (soft delete)
 * @param {string} storeUuid - UUID da loja
 * @param {number} id - ID da campanha
 * @returns {Promise<boolean>} Sucesso da operação
 */
export async function excluirCampanha(storeUuid, id) {
  try {
    // ✅ V2: Usar endpoint com storeUuid na URL
    // Soft delete: campanha não é removida do banco, apenas marcada como deletada
    await axios.delete(`${BASE_URL}/api/stores/${storeUuid}/campaigns/${id}`)
    return true
  } catch (error) {
    console.error('Erro ao excluir campanha:', error)
    throw error
  }
}

/**
 * Lista campanhas deletadas de uma loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de campanhas deletadas
 */
export async function listarCampanhasDeletadas(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/stores/${storeUuid}/campaigns`, {
      params: { include_deleted: 'true' },
    })

    console.log('📦 Todas as campanhas (com deleted_at):', response.data.data?.slice(0, 2))

    // Filtrar apenas campanhas com deleted_at não nulo
    const deletadas = (response.data.data || []).filter(c => c.deleted_at !== null)

    console.log('🗑️ Campanhas deletadas filtradas:', deletadas)

    return deletadas
  } catch (error) {
    console.error('Erro ao listar campanhas deletadas:', error)
    throw error
  }
}

/**
 * Busca estatísticas/métricas de todas as campanhas da loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Estatísticas das campanhas
 */
export async function buscarEstatisticasCampanhas(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/campaigns/stats`, {
      params: {
        store_uuid: storeUuid,
      },
    })

    return response.data || {}
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error)
    throw error
  }
}

/**
 * Busca métricas detalhadas de uma campanha específica
 * @param {string} storeUuid - UUID da loja
 * @param {number} campanhaId - ID da campanha
 * @returns {Promise<Object>} Métricas da campanha
 */
export async function buscarMetricasCampanha(storeUuid, campanhaId) {
  try {
    // ✅ V2: Usar endpoint com storeUuid na URL
    const response = await axios.get(`${BASE_URL}/api/stores/${storeUuid}/campaigns/${campanhaId}/stats`)
    return response.data
  } catch (error) {
    console.error('Erro ao buscar métricas da campanha:', error)
    throw error
  }
}

/**
 * Ativa ou desativa uma campanha
 * @param {string} storeUuid - UUID da loja
 * @param {number} campanhaId - ID da campanha
 * @param {boolean} isActive - Status desejado (true = ativo, false = inativo)
 * @returns {Promise<Object>} Campanha atualizada
 */
export async function toggleCampanhaAtiva(storeUuid, campanhaId, isActive) {
  try {
    const payload = {
      is_active: Boolean(isActive),
    }

    console.log(`🔄 Toggle campanha ${campanhaId}:`, payload)

    // ✅ V2: Usar endpoint com storeUuid na URL
    const response = await axios.patch(`${BASE_URL}/api/stores/${storeUuid}/campaigns/${campanhaId}/toggle`, payload)

    console.log('✅ Toggle realizado:', response.data)
    return response.data
  } catch (error) {
    console.error('❌ Erro ao alterar status da campanha:', error)
    console.error('❌ Detalhes:', error.response?.data)
    throw error
  }
}

/**
 * Cancela uma execução ativa de campanha
 * @param {string} storeUuid - UUID da loja
 * @param {number} campanhaId - ID da campanha
 * @param {number} runId - ID da execução a ser cancelada
 * @returns {Promise<Object>} Resultado do cancelamento
 */
export async function cancelarExecucaoCampanha(storeUuid, campanhaId, runId) {
  try {
    const response = await axios.post(`${BASE_URL}/api/stores/${storeUuid}/campaigns/${campanhaId}/runs/${runId}/cancel`)

    console.log('✅ Execução cancelada:', response.data)
    return response.data
  } catch (error) {
    console.error('❌ Erro ao cancelar execução:', error)
    console.error('❌ Detalhes:', error.response?.data)
    throw error
  }
}

/**
 * Faz upload de uma imagem
 * @param {File} file - Arquivo de imagem
 * @param {Function} onProgress - Callback de progresso
 * @returns {Promise<string>} URL da imagem
 */
export async function uploadImagem(file, onProgress) {
  try {
    const formData = new FormData()
    formData.append('image', file)

    // TODO: Implementar endpoint de upload
    // const response = await axios.post(`${BASE_URL}/api/upload`, formData, {
    //   headers: {
    //     'Content-Type': 'multipart/form-data'
    //   },
    //   onUploadProgress: (progressEvent) => {
    //     if (onProgress) {
    //       const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
    //       onProgress(percentCompleted)
    //     }
    //   }
    // })
    // return response.data.url

    // Simulação de upload
    return new Promise(resolve => {
      let progress = 0
      const interval = setInterval(() => {
        progress += 10
        if (onProgress) onProgress(progress)

        if (progress >= 100) {
          clearInterval(interval)
          // Retornar URL temporária (data URL)
          const reader = new FileReader()
          reader.onload = e => resolve(e.target.result)
          reader.readAsDataURL(file)
        }
      }, 100)
    })
  } catch (error) {
    console.error('Erro ao fazer upload:', error)
    throw error
  }
}

/**
 * Busca o status de qualidade do telefone do WhatsApp
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Status de qualidade do telefone
 */
export async function buscarStatusTelefone(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/stores/${storeUuid}/campaigns/phone-status`)
    return response.data
  } catch (error) {
    console.error('Erro ao buscar status do telefone:', error)
    throw error
  }
}

/**
 * Busca os pedidos/conversões de uma campanha específica
 * @param {string} storeUuid - UUID da loja
 * @param {number} campanhaId - ID da campanha
 * @returns {Promise<Array>} Lista de pedidos da campanha
 */
export async function buscarPedidosCampanha(storeUuid, campanhaId) {
  try {
    const response = await axios.get(`${BASE_URL}/api/stores/${storeUuid}/campaigns/${campanhaId}/orders`)
    return response.data
  } catch (error) {
    console.error('Erro ao buscar pedidos da campanha:', error)
    throw error
  }
}

/**
 * Vincula ou atualiza o código de cupom da campanha (mc-analytics).
 * @param {string} storeUuid
 * @param {number|string} campanhaId
 * @param {string} couponCode
 */
export async function atualizarCupomCampanha(storeUuid, campanhaId, couponCode) {
  const response = await axios.put(
    `${BASE_URL}/api/stores/${storeUuid}/campaigns/${campanhaId}`,
    { coupon_code: String(couponCode || '').trim() },
  )
  return response.data
}

/**
 * Busca dashboard administrativo com métricas do sistema
 * @param {Object} params - Parâmetros de filtro
 * @param {string} params.start_date - Data inicial (opcional, formato: YYYY-MM-DD)
 * @param {string} params.end_date - Data final (opcional, formato: YYYY-MM-DD)
 * @param {number} params.store_id - ID da loja para filtrar (opcional)
 * @returns {Promise<Object>} Dados do dashboard administrativo
 */
export async function buscarDashboardAdmin(params = {}) {
  try {
    const queryParams = {}
    if (params.start_date) queryParams.start_date = params.start_date
    if (params.end_date) queryParams.end_date = params.end_date
    if (params.store_id) queryParams.store_id = params.store_id

    const response = await axios.get(`${BASE_URL}/api/admin/dashboard`, {
      params: queryParams,
    })
    return response.data
  } catch (error) {
    console.error('Erro ao buscar dashboard administrativo:', error)
    throw error
  }
}

/**
 * E.164 para API WhatsApp (Brasil): se o usuário passar só DDD+número (10–11 dígitos),
 * prefixa 55. Números que já começam com 55 ou outros formatos internacionais não são alterados.
 * @param {string} raw
 * @returns {string}
 */
export function normalizarTelefoneE164Brasil(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  if (d.length >= 10 && d.length <= 11 && !d.startsWith('55')) {
    return `55${d}`
  }
  return d
}

/**
 * Envia mensagem de teste para um número específico
 * A mídia (imagem/vídeo) e UTM são extraídos automaticamente do template
 * @param {string} storeUuid - UUID da loja
 * @param {Object} dados - Dados do envio de teste
 * @param {string} dados.template_name - Nome do template
 * @param {string} dados.phone - Número de telefone (formato: 5511999999999)
 * @returns {Promise<Object>} Resultado do envio
 */
export async function enviarMensagemTeste(storeUuid, dados) {
  try {
    const phone = normalizarTelefoneE164Brasil(dados.phone)
    const response = await axios.post(`${BASE_URL}/api/stores/${storeUuid}/campaigns/send-test`, {
      template_name: dados.template_name,
      phone,
    })
    return response.data
  } catch (error) {
    console.error('Erro ao enviar mensagem de teste:', error)
    throw error
  }
}
